import { useCallback, useEffect, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { getIframelyData } from '#src/app/shared/api/medias';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { getMediaHitsPage } from '../../../api/getMediaHitsPage';
import type { MediaHitComment, MediaHitEmbed } from '../../../shared/context';

const HITS_PER_PAGE = 10;

type Status = 'error' | 'loaded' | 'loading' | 'loadingMore';

type Result = ModelHookResult<
	{
		embeds: readonly MediaHitEmbed[];
		status: Status;
		hasNextPage: boolean;
		hasLoadedMore: boolean;
	},
	{
		loadMore: () => void;
		removeMedia: (mediaId: string) => void;
	}
>;

const toLoadingEmbed = ({ id, url, mediaSource }: MediaHitComment): MediaHitEmbed => ({
	id,
	url,
	mediaSource,
	status: 'loading',
	html: '',
});

export function useMediaHitsModalList(
	isOpen: boolean,
	onEmbedsLoaded?: (embeds: readonly MediaHitEmbed[]) => void,
): Result {
	const {
		data: {
			petition: { id: petitionId },
			mediaHitsPage: { mediaHits: previewMediaHits, endCursor: previewEndCursor },
		},
	} = usePetitionDetailsPageData();
	const utilityContext = useUtilityContext();
	const { track } = useCampaignTracking();
	const [embeds, setEmbeds] = useState<readonly MediaHitEmbed[]>([]);
	// undefined = not seeded yet, string = next page cursor, null = exhausted
	const [cursor, setCursor] = useState<string | null | undefined>(undefined);
	const [status, setStatus] = useState<Status>('loading');
	const [hasLoadedMore, setHasLoadedMore] = useState(false);

	const resolveEmbed = useCallback(
		async ({ id: mediaHitId, url }: { id: string; url: string }) => {
			try {
				const { html } = await getIframelyData({ url, format: 'SMALL' }, utilityContext);
				setEmbeds((loaded) =>
					loaded.map((embed) => (embed.id === mediaHitId ? { ...embed, status: 'valid', html } : embed)),
				);
			} catch {
				// Do not throw when iframely fails to generate an embed, mark the single embed invalid and let the rest of the list resolve independently.
				setEmbeds((loaded) =>
					loaded.map((embed) => (embed.id === mediaHitId ? { ...embed, status: 'invalid', html: '' } : embed)),
				);
				track('media_hit_iframely_embed_failure', { petition_id: petitionId, media_url: url });
			}
		},
		[utilityContext, track, petitionId],
	);

	// Seeds from the preview's already-fetched first page (`usePetitionDetailsPageData`) instead of
	// re-fetching it: the preview and the modal share the same `mediaHitsConnection`, so pagination
	// continues from the preview's own `endCursor` rather than starting over at item 1.
	const seedFirstPage = useCallback(() => {
		const seededEmbeds = previewMediaHits.map(toLoadingEmbed);
		setEmbeds(seededEmbeds);
		setCursor(previewEndCursor);
		setStatus('loaded');
		setHasLoadedMore(false);
		onEmbedsLoaded?.(seededEmbeds);
		previewMediaHits.forEach((hit) => void resolveEmbed(hit));
	}, [previewMediaHits, previewEndCursor, onEmbedsLoaded, resolveEmbed]);

	const loadPage = useCallback(
		async (after: string) => {
			try {
				const page = await getMediaHitsPage(petitionId, { first: HITS_PER_PAGE, cursor: after }, utilityContext);
				if (!page) throw new Error('Failed to get media hits');
				const newEmbeds = page.mediaHits.map(toLoadingEmbed);
				setEmbeds((loaded) => [...loaded, ...newEmbeds]);
				setCursor(page.endCursor);
				setStatus('loaded');
				setHasLoadedMore(true);
				onEmbedsLoaded?.(newEmbeds);
				// fire each embed fetch independently so one slow article doesn't hold the list
				page.mediaHits.forEach((hit) => void resolveEmbed(hit));
			} catch {
				setStatus('error');
				setHasLoadedMore(false);
			}
		},
		[petitionId, utilityContext, resolveEmbed, onEmbedsLoaded],
	);

	// seed lazily on the FIRST open only — reopening re-seeds from the same preview data, no refetch
	const requestedRef = useRef(false);
	useEffect(() => {
		if (!isOpen || requestedRef.current) return;
		requestedRef.current = true;

		seedFirstPage();
	}, [isOpen, seedFirstPage]);

	useEffect(() => {
		if (isOpen || status !== 'error') return;
		requestedRef.current = false;
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setStatus('loading');
	}, [isOpen, status]);

	const loadMore = useCallback(() => {
		if (!cursor) return;
		setStatus('loadingMore');
		void loadPage(cursor);
	}, [cursor, loadPage]);

	const removeMedia = useCallback((mediaId: string) => {
		setEmbeds((loaded) => loaded.filter((embed) => embed.id !== mediaId));
	}, []);

	return {
		data: { embeds, status, hasNextPage: Boolean(cursor), hasLoadedMore },
		actions: { loadMore, removeMedia },
	};
}
